<template>
  <LegalPageLayout
    title="Contact Us"
    :lastUpdated="lastUpdated"
    :showTableOfContents="true"
  >
    <div class="contact-content">
      <!-- Introduction Section -->
      <section id="introduction" class="mb-8">
        <h2 class="text-2xl font-semibold mb-4 text-gray-800">Get in Touch</h2>
        <p class="text-gray-600 leading-relaxed mb-4">
          We're here to help! Whether you have questions about our wallpapers, need technical support, 
          or want to discuss business opportunities, we'd love to hear from you. Choose the most 
          appropriate contact method below for the fastest response.
        </p>
      </section>

      <!-- Contact Methods Section -->
      <section id="contact-methods" class="mb-8">
        <h2 class="text-2xl font-semibold mb-6 text-gray-800">Contact Methods</h2>
        
        <div class="grid gap-4 sm:gap-6 lg:grid-cols-2 xl:gap-8" role="list">
          <div 
            v-for="contact in contactMethods" 
            :key="contact.category"
            class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
            role="listitem"
          >
            <h3 
              class="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-gray-800"
              :id="`contact-${contact.category.toLowerCase().replace(/\s+/g, '-')}-heading`"
            >
              {{ contact.category }}
            </h3>
            <p 
              class="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base leading-relaxed"
              :aria-describedby="`contact-${contact.category.toLowerCase().replace(/\s+/g, '-')}-heading`"
            >
              {{ contact.description }}
            </p>
            
            <div class="mb-3 sm:mb-4">
              <a 
                :href="`mailto:${contact.email}?subject=${encodeURIComponent(contact.category + ' - Wallpaper Hub')}`"
                class="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 p-2 sm:p-3 rounded-lg hover:bg-blue-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] w-full sm:w-auto break-all sm:break-normal"
                :aria-label="`Send email to ${contact.category} at ${contact.email}`"
                role="link"
              >
                <svg 
                  class="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <span class="text-sm sm:text-base">{{ contact.email }}</span>
              </a>
            </div>
            
            <div 
              class="text-xs sm:text-sm text-gray-500 bg-gray-50 p-2 sm:p-3 rounded-md"
              role="note"
              :aria-label="`Expected response time for ${contact.category}: ${contact.responseTime}`"
            >
              <span class="font-medium">Response Time:</span> 
              <span class="text-gray-700">{{ contact.responseTime }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ Section -->
      <section id="faq" class="mb-6 sm:mb-8" role="region" aria-labelledby="faq-heading">
        <h2 id="faq-heading" class="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-gray-800">Frequently Asked Questions</h2>
        
        <div class="space-y-4 sm:space-y-6" role="list">
          <div 
            v-for="(faq, index) in faqItems" 
            :key="faq.question"
            class="bg-gray-50 hover:bg-gray-100 transition-colors duration-200 rounded-lg p-4 sm:p-6"
            role="listitem"
          >
            <h3 
              class="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800 leading-tight"
              :id="`faq-question-${index + 1}`"
            >
              {{ faq.question }}
            </h3>
            <p 
              class="text-gray-600 leading-relaxed text-sm sm:text-base"
              :aria-labelledby="`faq-question-${index + 1}`"
              role="definition"
            >
              {{ faq.answer }}
            </p>
          </div>
        </div>
      </section>

      <!-- Response Expectations Section -->
      <section id="response-expectations" class="mb-6 sm:mb-8" role="region" aria-labelledby="response-expectations-heading">
        <h2 id="response-expectations-heading" class="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">Response Expectations</h2>
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6" role="note" aria-label="Response time information for different contact categories">
          <ul class="space-y-2 sm:space-y-3 text-gray-700 text-sm sm:text-base" role="list">
            <li class="flex items-start" role="listitem">
              <span class="text-blue-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span><strong class="font-semibold">General Support:</strong> We typically respond within 24-48 hours during business days</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="text-blue-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span><strong class="font-semibold">Technical Issues:</strong> Critical issues are addressed within 4-8 hours, others within 24 hours</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="text-blue-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span><strong class="font-semibold">Business Inquiries:</strong> We respond to partnership and business requests within 2-3 business days</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="text-blue-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span><strong class="font-semibold">Legal/Privacy:</strong> Privacy and legal concerns are handled within 1-2 business days</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Additional Information Section -->
      <section id="additional-info" class="mb-6 sm:mb-8" role="region" aria-labelledby="additional-info-heading">
        <h2 id="additional-info-heading" class="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-gray-800">Additional Information</h2>
        <div class="space-y-4 sm:space-y-6">
          <p class="text-gray-600 leading-relaxed text-sm sm:text-base">
            When contacting us, please include as much relevant information as possible to help us 
            assist you quickly and effectively:
          </p>
          <ul class="text-gray-600 space-y-2 sm:space-y-3 ml-4 sm:ml-6 text-sm sm:text-base" role="list" aria-label="Information to include when contacting support">
            <li class="flex items-start" role="listitem">
              <span class="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span>Your account email (if applicable)</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span>Device and browser information (for technical issues)</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span>Screenshots or error messages (if relevant)</span>
            </li>
            <li class="flex items-start" role="listitem">
              <span class="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
              <span>Detailed description of your question or issue</span>
            </li>
          </ul>
          
          <div 
            class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-5" 
            role="alert" 
            aria-labelledby="urgent-issues-heading"
          >
            <p class="text-gray-700 leading-relaxed text-sm sm:text-base">
              <strong id="urgent-issues-heading" class="font-semibold text-yellow-800">Urgent Issues:</strong> For urgent technical issues that affect the availability of our service, please use our 
              technical support email and mark your message as "URGENT" in the subject line.
            </p>
          </div>
        </div>
      </section>
    </div>
  </LegalPageLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import LegalPageLayout from '@/components/LegalPageLayout.vue'
import { seoManager } from '@/utils/seoUtils'

// Meta information
const lastUpdated = ref('January 15, 2025')

// Contact methods data
const contactMethods = ref([
  {
    category: 'General Support',
    email: 'collinsodeny@gmail.com',
    description: 'Questions about using our service, account issues, or general inquiries.',
    responseTime: '24-48 hours'
  },
  {
    category: 'Technical Issues',
    email: 'collinsodeny@gmail.com',
    description: 'Bug reports, technical problems, or issues with downloading wallpapers.',
    responseTime: '4-24 hours'
  },
  {
    category: 'Business Inquiries',
    email: 'collinsodeny@gmail.com',
    description: 'Partnership opportunities, advertising, or business-related questions.',
    responseTime: '2-3 business days'
  },
  {
    category: 'Privacy & Legal',
    email: 'collinsodeny@gmail.com',
    description: 'Privacy concerns, legal questions, or data protection inquiries.',
    responseTime: '1-2 business days'
  }
])

// FAQ data
const faqItems = ref([
  {
    question: 'How do I download wallpapers?',
    answer: 'Simply browse our collection, click on any wallpaper you like, and use the download button. You can choose from different resolutions to match your device.'
  },
  {
    question: 'Are the wallpapers free to use?',
    answer: 'Yes, all wallpapers on our platform are free for personal use. For commercial use, please check the specific license information or contact our business team.'
  },
  {
    question: 'Can I upload my own wallpapers?',
    answer: 'Currently, wallpaper uploads are managed by our admin team. If you\'re interested in contributing wallpapers, please contact our business team to discuss partnership opportunities.'
  },
  {
    question: 'Why isn\'t a wallpaper downloading?',
    answer: 'This could be due to browser settings, network issues, or temporary server problems. Try refreshing the page, checking your internet connection, or contact our technical support team.'
  },
  {
    question: 'How do I report inappropriate content?',
    answer: 'If you find content that violates our terms of service, please contact our support team immediately with details about the specific wallpaper and the nature of the issue.'
  }
])

// SEO Meta tags
onMounted(() => {
  // Set comprehensive SEO tags using the new SEO manager
  const metaTags = {
    title: 'Contact Us - WallpaperHub',
    description: 'Get in touch with WallpaperHub for support, technical issues, business inquiries, or privacy concerns. Multiple contact methods available with fast response times and dedicated support teams.',
    keywords: 'contact us, customer support, technical support, business inquiries, wallpaper app support, help center, contact information, support team, get in touch, customer service',
    robots: 'index, follow',
    ogType: 'website'
  };
  
  seoManager.applyMetaTags(metaTags);
})
</script>

<style scoped>
/* Print styles */
@media print {
  .contact-content {
    @apply text-black;
  }
  
  a[href^="mailto:"] {
    @apply text-black no-underline;
  }
  
  a[href^="mailto:"]:after {
    content: " (" attr(href) ")";
    @apply text-sm text-gray-600;
  }
}
</style>